import React, { useState, useEffect } from "react";
import {
	Grid,
	Box,
	IconButton,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Typography,
} from "@mui/material";
import { useGridApiRef, gridRowNodeSelector } from "@mui/x-data-grid-pro";
import CloseIcon from '@mui/icons-material/Close';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import MoveTo from "../../../container/Documents/Modals/MoveTo";
import Delete from "../../../container/Documents/Modals/Delete";
import CreateFolder from "../../../container/Documents/Modals/CreateFolder";
import UploadFile from "../../../container/Documents/Modals/UploadFile";
import DocumentsTable from "../../../container/Documents/DocumentsTable";
import PreviewDetails from "../../../container/Documents/PreviewDetails";
import SearchBar from "../../../container/Documents/Filters/SearchBar";
import axios from "axios";
import { APIURL } from "../../../config";
import moment from "moment";

const EmployeeDocuments = (props) => {
	const [rows, setRows] = useState([]);
	const [show, setShow] = useState(false);
	const [modal, setModal] = useState(null);
	const [modalData, setModalData] = useState(null);
	const [loading, setLoading] = useState(false);

	const [open, setOpen] = useState(false);
	const [previewData, setPreviewData] = useState(null);

	const [rootDirectory, setRootDirectory] = useState(null);
	const [largestId, setLargestId] = useState(0);
	const [filterModel, setFilterModel] = useState({ items: [], logicOperator: "and"});
	const [isExpanded, setIsExpanded] = useState(false);
	const [data, setData] = useState([]);
	const [expandedRowIds, setExpandedRowIds] = useState([]);

	const apiRef = useGridApiRef();

	const handlePreviewOpen = (data) => {
		setPreviewData(data);
		setOpen(true);
	};

	const handlePreviewClose = () => {
		setPreviewData(null);
		setOpen(false);
	};
	
	const handleModalOpen = (modal, data=null) => {
		setShow(true);
		setModal(modal);
		setModalData(data);
	};
	
	const handleModalClose = () => {
		setShow(false);
		setTimeout(() => {
			setModal(null);
			setModalData(null);
		}, 500);
	};

	const ensureFolderStructure = async (allDirectories) => {
		let secureFolder = allDirectories.find(item => 
			item.directory_name === "Secure" && item.file_path.endsWith("/Secure")
		);
		
		if (!secureFolder) {
			const rootFolder = allDirectories.find(item => 
				item.directory_name === "root" || item.parent_id === null
			);
			
			if (rootFolder) {
				const createSecure = await axios.post(`${APIURL}/directories`, {
					name: "Secure",
					parent_id: rootFolder.directory_id,
					extID: props.employee.emp_company_id,
					created_by: props.employee.emp_id,
				});
				if (createSecure.data?.code === 200) {
					// Refresh directory data to get proper file_path
					const refreshedData = await axios.get(APIURL + "/directories/all/" + props.employee.emp_company_id);
					secureFolder = refreshedData.data.find(item => 
						item.directory_id === createSecure.data?.id
					);
					if (secureFolder) {
						allDirectories.push(secureFolder);
					}
				}
			}
		}

		let employeeDocsFolder = allDirectories.find(item => 
			item.directory_name === "Employee Documents" && 
			item.file_path.includes("/Secure/Employee Documents")
		);
		
		if (!employeeDocsFolder && secureFolder) {
			const createEmpDocs = await axios.post(`${APIURL}/directories`, {
				name: "Employee Documents",
				parent_id: secureFolder.directory_id,
				extID: props.employee.emp_company_id,
				created_by: props.employee.emp_id,
			});
			if (createEmpDocs.data?.code === 200) {
				// Refresh directory data to get proper file_path
				const refreshedData = await axios.get(APIURL + "/directories/all/" + props.employee.emp_company_id);
				employeeDocsFolder = refreshedData.data.find(item => 
					item.directory_id === createEmpDocs.data?.id
				);
				if (employeeDocsFolder) {
					allDirectories.push(employeeDocsFolder);
				}
			}
		}

		const employeeFolderName = `${props.employee.emp_first_name} ${props.employee.emp_last_name} - ${props.employee.emp_id}`;
		
		let currentEmployeeFolder = allDirectories.find(item => 
			item.directory_name === employeeFolderName && 
			item.file_path.includes("/Employee Documents/")
		);
		
		if (!currentEmployeeFolder && employeeDocsFolder) {
			const createEmpFolder = await axios.post(`${APIURL}/directories`, {
				name: employeeFolderName,
				parent_id: employeeDocsFolder.directory_id,
				extID: props.employee.emp_company_id,
				created_by: props.employee.emp_id,
			});
			if (createEmpFolder.data?.code === 200) {
				// Refresh directory data to get proper file_path
				const refreshedData = await axios.get(APIURL + "/directories/all/" + props.employee.emp_company_id);
				currentEmployeeFolder = refreshedData.data.find(item => 
					item.directory_id === createEmpFolder.data?.id
				);
				if (currentEmployeeFolder) {
					allDirectories.push(currentEmployeeFolder);
				}
			}
		}

		return { allDirectories, currentEmployeeFolder };
	};

	const getData = async () => {
		if (!props.employee?.emp_company_id) return;
		
		try {
			let directoryData = await axios.get(APIURL + "/directories/all/" + props.employee.emp_company_id);
			
			let allDirectories = [];
			if (directoryData.data.length > 0) {
				allDirectories = [...directoryData.data];
			}
			
			const { currentEmployeeFolder } = await ensureFolderStructure(allDirectories);
			
			const updatedDirectoryData = await axios.get(APIURL + "/directories/all/" + props.employee.emp_company_id);
			setData(updatedDirectoryData.data);
			
			// Get virtual data for the employee
			let virtualData = await axios.get(APIURL + "/directories/virtual/" + props.employee.emp_company_id, {
				params: {
					employee_id: props.employee.emp_id,
				}
			});
			if (virtualData.data.length > 0) {
				setData(prev => [...prev, ...virtualData.data]);
			}

			if (currentEmployeeFolder) {
				const pathSegments = currentEmployeeFolder.file_path.split('/').filter(Boolean);
				const directoryIdsToExpand = [];
				
				const pathsWithLeadingSlash = [];
				for (let i = 1; i < pathSegments.length; i++) {
					const partialPath = '/' + pathSegments.slice(0, i + 1).join('/');
					pathsWithLeadingSlash.push(partialPath);
				}
				
				const pathsWithoutLeadingSlash = pathsWithLeadingSlash.map(p => p.substring(1));
				
				updatedDirectoryData.data.forEach((dir) => {
					if (!dir.file_name && (pathsWithLeadingSlash.includes(dir.file_path) || pathsWithoutLeadingSlash.includes(dir.file_path))) {
						directoryIdsToExpand.push(dir.directory_id);
					}
				});
				
				// Also expand the employee's own folder
				directoryIdsToExpand.push(currentEmployeeFolder.directory_id);
				
				setExpandedRowIds(directoryIdsToExpand);
			}
		} catch (error) {
			console.error("Error fetching employee documents:", error);
		}
	};



	useEffect(() => {
		if (props.employee?.emp_id && props.employee?.emp_company_id) {
			getData();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.employee]);

	useEffect(() => {
		if (!props.employee?.emp_id) return;

		const employeeFolderName = `${props.employee.emp_first_name} ${props.employee.emp_last_name} - ${props.employee.emp_id}`;
		
        let processedData = [...data];
		
		// Filter to only include the employee's folder and its contents
		processedData = processedData.filter((item) => {
			const path = item.file_path.split('/').filter(Boolean);
			// Include items that are within the employee's folder
			const isInEmployeeFolder = path.includes(employeeFolderName);
			// Or the employee folder itself
			const isEmployeeFolder = item.directory_name === employeeFolderName && 
				item.file_path.includes("/Employee Documents/");
			return isInEmployeeFolder || isEmployeeFolder;
		});

		let gapRows = new Map();

		processedData.forEach((item, index) => {
			if(item.file_name !== null) {
				let newItem = {
					...item,
					file_name: null,
					emp_name: null,
					cus_name: null,
					upload_date: null,
					file_id: null,
					file_url: null,
					file_ext: null,
					created_by: null,
				};
				gapRows.set(item.directory_id, newItem);
			}
		});

		gapRows.forEach((value, key) => {
			if(!processedData.find((item) => item.file_name === null && item.directory_id === key))
				processedData.push(value);
		});

        let processedRows = processedData.map((item, index) => {
			let pathArray = item.file_path.split('/').filter(Boolean);
			
			// Find the index of the employee folder name in the path
			const employeeFolderIndex = pathArray.indexOf(employeeFolderName);
			
			// If this is the employee folder or its contents, adjust the path
			if (employeeFolderIndex !== -1) {
				// Remove everything up to and including the employee folder
				pathArray = pathArray.slice(employeeFolderIndex + 1);
			}
			
			if(item.file_name) {
				pathArray.push(item.file_name + '.' + item.file_url?.split('.').pop());
			}
			
			return {
				file_path: pathArray,
				file_name: item.file_name,
				file_id: item.file_id,
				file_url: item.file_url,
				file_ext: item.file_url?.split('.').pop() || null,
				directory_id: item.directory_id,
				directory_name: item.directory_name,
				id: index,
				client: item.cus_name,
				owner: item.emp_name,
				extID: item.extID,
				upload_date: item.upload_date,
				directory_added_date: item.directory_added_date,
				locked: item.locked,
				created_by: item.file_name ? item.uploaded_by : item.created_by,
			}
    	});
		
		// The employee folder itself becomes the root
		let rootDir = processedRows.find((item) => 
			item.directory_name === employeeFolderName && item.file_path.length === 0
		);
		
		setRootDirectory(rootDir);
		processedRows = processedRows.filter((item) => item.file_path.length > 0);
		processedRows.sort((a, b) => a.file_name ? 1 : -1);
		setRows(processedRows);
		setLargestId(processedRows.length > 0 ? processedRows.length + 1 : 0);
    }, [data, props.employee]);

	useEffect(() => {
		if (apiRef.current) {	
			rows.forEach((item) => {
				if (gridRowNodeSelector(apiRef, item.id).type === 'group') {
					apiRef.current.setRowChildrenExpansion(item.id, isExpanded);
				}
			});
		}
	}, [isExpanded, apiRef, rows]);

	useEffect(() => {
		if (apiRef.current && expandedRowIds.length > 0 && rows.length > 0) {
			const timer = setTimeout(() => {
				rows.forEach((row) => {
					if (expandedRowIds.includes(row.directory_id) && !row.file_name) {
						try {
							const node = gridRowNodeSelector(apiRef, row.id);
							if (node?.type === 'group') {
								apiRef.current.setRowChildrenExpansion(row.id, true);
							}
						} catch (e) {
							// Silently handle expansion errors
						}
					}
				});
			}, 500);
			
			return () => clearTimeout(timer);
		}
	}, [apiRef, rows, expandedRowIds]);

	const onMove = async (type, fromRow, toRow=rootDirectory) => {
		setLoading(true);
		if(toRow === null) {
			toRow = rootDirectory;
		}
		if (type === "file") {
			let updateFileDirectory = await axios.put(`${APIURL}/artifacts/${fromRow.file_id}/directory`, { 
				directory_id: toRow.directory_id, 
				emp_id: props.employee.emp_id, 
				comp_id: props.employee.emp_company_id
			});
			if(updateFileDirectory.data?.code === 200) {
				let newRows = [...rows];
				newRows.forEach((item, index) => {
					if(item.file_id === fromRow.file_id) {
						newRows[index] = {
							...toRow,
							...fromRow,
							file_path: toRow.file_path.concat(fromRow.file_name)
						}
					}
				});
				setRows(newRows);
				setLoading(false);
				handleModalClose();
			}
		} else {
			let updateDirectory = await axios.put(`${APIURL}/directories/${fromRow.directory_id}`,
			    { 
					parent_id: toRow.directory_id,
					directory_id: toRow.directory_id, 
					emp_id: props.employee.emp_id, 
					comp_id: props.employee.emp_company_id,
					name: fromRow.directory_name,
					extID: fromRow.extID,
				}
			);
			if(updateDirectory.data?.code === 200) {
				await getData();
				setLoading(false);
				handleModalClose();
			}
		}
	};

	const onDelete = async (type, row) => {
		setLoading(true);
		if (type === "file") {
			let deleteFile = await axios.delete(APIURL+'/artifacts', { 
				data: { 
					id: row.file_id, 
					emp_id: props.employee.emp_id, 
					comp_id: props.employee.emp_company_id 
				} 
			});
			if(deleteFile.data?.code === 200) {
				let newRows = rows.filter((item) => item.file_id !== row.file_id);
				setRows(newRows);
				setLoading(false);
				handleModalClose();
			}
		} else {
			let deleteDirectory = await axios.delete(`${APIURL}/directories/${row.directory_id}`, {
				data: { 
					emp_id: props.employee.emp_id, 
					comp_id: props.employee.emp_company_id 
				}
			});
			if(deleteDirectory.data?.code === 200) {
				let newRows = rows.filter((item) => item.directory_id !== row.directory_id);
				setRows(newRows);
				setLoading(false);
				handleModalClose();
			}
		}
	}

	const createFolder = async (name, parentRow) => {
		setLoading(true);
		if(parentRow === null) {
			parentRow = rootDirectory;
		}
		let createDirectory = await axios.post(`${APIURL}/directories`, {
			name: name,
			parent_id: parentRow.directory_id,
			extID: props.employee.emp_company_id,
			created_by: props.employee.emp_id,
		});
		if(createDirectory.data?.code === 200) {
			let newRows = [...rows];
			newRows.push({
				...parentRow,
				directory_id: createDirectory.data?.id,
				directory_name: name,
				file_path: parentRow.file_path.concat(name),
				extID: props.employee.emp_company_id,
				id: largestId + 1,
				locked: false,
				created_by: props.employee.emp_id,
			});
			setLargestId(largestId + 1);
			setRows(newRows);
			setLoading(false);
			handleModalClose();
		}
	}

	const onUpload = async (file, parentRow) => {
		setLoading(true);
		if(parentRow === null) {
			parentRow = rootDirectory;
		}
		let formData = new FormData();
		formData.append('file_name', file.name.replace(/(\.[^/.]+)+$/, ""));
		formData.append('directory_id', parentRow.directory_id);
		formData.append('extID', props.employee.emp_company_id);
		formData.append("cus_id", null);
		formData.append("proj_id", null);
		formData.append("task_id", null);
		formData.append("emp_id", props.employee.emp_id);
		formData.append("snapshot", file, file.name);
		let uploadFile = await axios.post(APIURL+"/artifacts", formData, { 
			headers: { "content-type": "multipart/form-data" }
		});
		if(uploadFile.data?.code === 200) {
			let newRows = [...rows];
			let fileName = file.name.replace(/(\.[^/.]+)+$/, "");
			newRows.push({
				...parentRow,
				file_id: uploadFile.data?.id,
				file_name: fileName,
				file_path: parentRow.file_path.concat(fileName),
				file_url: uploadFile.data?.file_url,
				file_ext: file.name.split('.').pop(),
				upload_date: moment().format("YYYY-MM-DD HH:mm:ss"),
				extID: props.employee.emp_company_id,
				id: largestId + 1,
			});
			setLargestId(largestId + 1);
			setRows(newRows);
			setLoading(false);
			handleModalClose();
		}
	}

 	const selectModal = (modal) => {
		switch (modal) {
			case "move":
				return <MoveTo 
					rows={rows} 
					fromFileDirectory={modalData} 
					onMove={onMove} 
					handleModalClose={handleModalClose} 
					loading={loading}
				/>;
			case "delete":
				return <Delete 
					data={modalData} 
					onDelete={onDelete} 
					handleModalClose={handleModalClose} 
					loading={loading}
				/>;
			case "createFolder":
				return <CreateFolder 
					rows={rows} 
					rootDirectory={rootDirectory} 
					createFolder={createFolder} 
					handleModalClose={handleModalClose} 
					loading={loading}
				/>;
			case "upload":
				return <UploadFile 
					rows={rows} 
					rootDirectory={rootDirectory} 
					onUpload={onUpload} 
					handleModalClose={handleModalClose} 
					loading={loading}
				/>;
			default:
				return null;
		}
	};

	return (
		<Box sx={{ 
			width: "100%", 
			display: 'flex',
			flexDirection: 'column',
			px: 2,
			py: 2,
			overflow: 'hidden'
		}}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
				<Typography variant="h6">
					Documents for {props.employee?.emp_first_name} {props.employee?.emp_last_name}
				</Typography>
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button variant="contained" startIcon={<CreateNewFolderOutlinedIcon />} onClick={() => handleModalOpen("createFolder")}>
						Create Folder
					</Button>
					<Button variant="contained" startIcon={<UploadFileOutlinedIcon />} onClick={() => handleModalOpen("upload")}>
						Upload File
					</Button>
				</Box>
			</Box>
			<Box sx={{ flexShrink: 0, mb: 2 }}>
				<Grid container sx={{ width: "100%" }}>
					<Grid size={{ xs: 12, md: 7, lg: 6 }}>
						<SearchBar 
							rows={rows} 
							apiRef={apiRef}
							setIsExpanded={setIsExpanded}
						/>
					</Grid>
				</Grid>
			</Box>
			<Box sx={{ 
				flex: 1, 
				width: "100%", 
				minHeight: 0,
				overflow: 'hidden'
			}}>
				<DocumentsTable
					rows={rows}
					handleModalOpen={handleModalOpen}
					handlePreviewOpen={handlePreviewOpen}
					filterModel={filterModel}
					setFilterModel={setFilterModel}
					apiRef={apiRef}
					height={420}
					user={{
						emp_id: props.employee?.emp_id,
						type: 'Employee'
					}}
				/>
			</Box>
			<PreviewDetails
				open={open}
				handlePreviewClose={handlePreviewClose}
				data={previewData}
			/>
			<Dialog open={show} onClose={handleModalClose}>
				<DialogActions>
					<IconButton
						aria-label="close"
						color="inherit"
						sx={{ p: 0.25 }}
						onClick={handleModalClose}
					>
						<CloseIcon />
					</IconButton>
				</DialogActions>
				<DialogContent sx={{ paddingTop: '0px', width: 370 }}>
					{selectModal(modal)}
				</DialogContent>
			</Dialog>
		</Box>
  	);
};

export default EmployeeDocuments;
